import { Component } from '@theme/component';

/**
 * @typedef {object} Refs
 * @property {HTMLElement} days
 * @property {HTMLElement} hours
 * @property {HTMLElement} minutes
 * @property {HTMLElement} seconds
 */

/**
 * Pure function: classifies a release-at value relative to "now".
 * Exported so it can be exercised directly by the Structural_Test_Suite
 * without needing a DOM/customElements environment.
 *
 * @param {string | null} releaseAt
 * @param {Date} now
 * @returns {
 *   | { kind: 'counting', days: number, hours: number, minutes: number, seconds: number }
 *   | { kind: 'expired' }
 *   | { kind: 'none' }
 *   | { kind: 'invalid' }
 * }
 */
export function classifyRelease(releaseAt, now) {
  if (!releaseAt) return { kind: 'none' };

  const target = new Date(releaseAt);
  if (Number.isNaN(target.getTime())) return { kind: 'invalid' };

  const diffMs = target.getTime() - now.getTime();
  if (diffMs <= 0) return { kind: 'expired' };

  const totalSeconds = Math.ceil(diffMs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return { kind: 'counting', days, hours, minutes, seconds };
}

/**
 * A custom element that renders a live D/H/M/S countdown toward a
 * `release-at` datetime attribute, and hides its enclosing release-status
 * block and restores commerce controls once the countdown expires or the
 * value is missing/unparsable.
 *
 * @extends {Component<Refs>}
 */
class ReleaseCountdown extends Component {
  requiredRefs = ['days', 'hours', 'minutes', 'seconds'];

  /** @type {number | undefined} */
  #intervalId;

  connectedCallback() {
    super.connectedCallback();
    this.#tick();
    this.#intervalId = window.setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.clearInterval(this.#intervalId);
  }

  #tick() {
    const state = classifyRelease(this.getAttribute('release-at'), new Date());

    if (state.kind !== 'counting') {
      this.dataset.state = state.kind;
      this.hidden = true;
      this.closest('[data-testid="release-status"]')?.setAttribute('hidden', '');
      this.closest('.product-information')?.removeAttribute('data-release-active');
      document.querySelectorAll('sticky-add-to-cart[data-release-active]').forEach((stickyAddToCart) => {
        stickyAddToCart.removeAttribute('data-release-active');
      });
      window.clearInterval(this.#intervalId);
      return;
    }

    this.dataset.state = 'counting';
    this.hidden = false;
    const { days, hours, minutes, seconds } = state;
    this.refs.days.textContent = String(days).padStart(2, '0');
    this.refs.hours.textContent = String(hours).padStart(2, '0');
    this.refs.minutes.textContent = String(minutes).padStart(2, '0');
    this.refs.seconds.textContent = String(seconds).padStart(2, '0');
  }
}

if (!customElements.get('release-countdown')) {
  customElements.define('release-countdown', ReleaseCountdown);
}
